import React, { useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { Footer } from '../components/Footer';
import { ScrollToTop } from '../components/ScrollToTop';
import { getNeighborhoodById } from '../data/neighborhoods';
import { getPlacesByNeighborhood } from '../data/places';
export function NeighborhoodDetailPage() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const neighborhood = id ? getNeighborhoodById(id) : undefined;
  const places = id ? getPlacesByNeighborhood(id) : [];
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);
  if (!neighborhood) {
    return <Navigate to="/neighborhoods" replace data-id="element-1180" />;
  }
  const regionColors: Record<string, {
    bg: string;
    text: string;
    border: string;
  }> = {
    Central: {
      bg: 'bg-blue-50',
      text: 'text-blue-900',
      border: 'border-blue-200'
    },
    North: {
      bg: 'bg-purple-50',
      text: 'text-purple-900',
      border: 'border-purple-200'
    },
    South: {
      bg: 'bg-green-50',
      text: 'text-green-900',
      border: 'border-green-200'
    },
    East: {
      bg: 'bg-orange-50',
      text: 'text-orange-900',
      border: 'border-orange-200'
    },
    West: {
      bg: 'bg-rose-50',
      text: 'text-rose-900',
      border: 'border-rose-200'
    }
  };
  const regionColor = regionColors[neighborhood.region];
  return <div className="min-h-screen bg-white" data-id="element-1181">
      {/* Hero - Text Only */}
      <div className="relative bg-gray-900 overflow-hidden" data-id="element-1182">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" data-id="element-1183"></div>

        <Link to="/neighborhoods" className="absolute top-8 left-8 bg-white/10 backdrop-blur-sm text-white px-6 py-3 hover:bg-white/20 transition-colors flex items-center gap-2 text-sm font-light uppercase tracking-widest border border-white/20 z-10" data-id="element-1184">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} data-id="element-1185" />
          All Estates
        </Link>

        <div className="relative max-w-7xl mx-auto px-8 lg:px-12 py-32 md:py-48" data-id="element-1186">
          <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.2,
          duration: 0.8
        }} data-id="element-1187">
            <div className="h-px w-16 bg-amber-500 mb-8" data-id="element-1188"></div>
            <div className="flex flex-wrap gap-4 mb-8" data-id="element-1189">
              <span className={`px-6 py-3 ${regionColor.bg} ${regionColor.text} text-sm font-light tracking-wider border ${regionColor.border}`} data-id="element-1190">
                {neighborhood.region} Region
              </span>
            </div>
            <h1 className="text-6xl md:text-8xl font-light text-white mb-8 leading-[1.1] tracking-tight" data-id="element-1191">
              {neighborhood.name}
            </h1>
            <p className="text-2xl text-white/80 font-light tracking-wide" data-id="element-1192">
              {neighborhood.character}
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 lg:px-12 py-20" data-id="element-1193">
        <div className="max-w-4xl space-y-16" data-id="element-1194">
          {/* Overview */}
          <section data-id="element-1195">
            <div className="h-px w-16 bg-amber-500 mb-8" data-id="element-1196"></div>
            <h2 className="text-5xl font-light text-gray-900 mb-8 tracking-tight" data-id="element-1197">
              Overview
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed font-light" data-id="element-1198">
              {neighborhood.description}
            </p>
          </section>

          {/* Highlights */}
          <section data-id="element-1199">
            <div className="h-px w-16 bg-amber-500 mb-8" data-id="element-1200"></div>
            <h2 className="text-5xl font-light text-gray-900 mb-8 tracking-tight" data-id="element-1201">
              Distinguished Features
            </h2>
            <div className="grid md:grid-cols-2 gap-6" data-id="element-1202">
              {neighborhood.highlights.map((highlight, index) => <motion.div key={index} className={`flex items-start gap-4 p-6 ${regionColor.bg} border ${regionColor.border}`} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.1 * index
            }} data-id="element-1203">
                  <div className="w-1 h-1 bg-amber-500 rounded-full mt-2 flex-shrink-0" data-id="element-1204" />
                  <p className="text-gray-700 font-light leading-relaxed" data-id="element-1205">
                    {highlight}
                  </p>
                </motion.div>)}
            </div>
          </section>

          {/* Best For */}
          <section data-id="element-1206">
            <div className="h-px w-16 bg-amber-500 mb-8" data-id="element-1207"></div>
            <h2 className="text-5xl font-light text-gray-900 mb-8 tracking-tight" data-id="element-1208">
              Ideal For
            </h2>
            <div className="flex flex-wrap gap-4" data-id="element-1209">
              {neighborhood.bestFor.map(item => <span key={item} className="px-6 py-3 border border-gray-200 text-gray-700 text-sm font-light tracking-wider" data-id="element-1210">
                  {item}
                </span>)}
            </div>
          </section>

          {/* Demographics */}
          <section data-id="element-1211">
            <div className="h-px w-16 bg-amber-500 mb-8" data-id="element-1212"></div>
            <h2 className="text-5xl font-light text-gray-900 mb-8 tracking-tight" data-id="element-1213">
              Community
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed font-light" data-id="element-1214">
              {neighborhood.demographics}
            </p>
          </section>

          {/* Accessibility */}
          <section data-id="element-1215">
            <div className="h-px w-16 bg-amber-500 mb-8" data-id="element-1216"></div>
            <h2 className="text-5xl font-light text-gray-900 mb-8 tracking-tight" data-id="element-1217">
              Connectivity
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed font-light" data-id="element-1218">
              {neighborhood.accessibility}
            </p>
          </section>

          {/* Places in Neighborhood */}
          {places.length > 0 && <section data-id="element-1219">
              <div className="h-px w-16 bg-amber-500 mb-8" data-id="element-1220"></div>
              <h2 className="text-5xl font-light text-gray-900 mb-12 tracking-tight" data-id="element-1221">
                Destinations in {neighborhood.name}
              </h2>
              <div className="space-y-8" data-id="element-1222">
                {places.map(place => <Link key={place.id} to={`/place/${place.id}`} className="block group" data-id="element-1223">
                    <div className="border border-gray-200 hover:border-gray-300 transition-all p-8" data-id="element-1224">
                      <span className="text-xs text-gray-500 uppercase tracking-widest mb-3 font-light block" data-id="element-1225">
                        {place.category}
                      </span>
                      <h3 className="text-3xl font-light text-gray-900 mb-4 group-hover:text-gray-600 transition-colors" data-id="element-1226">
                        {place.name}
                      </h3>
                      <p className="text-gray-600 font-light leading-relaxed" data-id="element-1227">
                        {place.description}
                      </p>
                    </div>
                  </Link>)}
              </div>
            </section>}
        </div>
      </div>

      <ScrollToTop data-id="element-1228" />
      <Footer data-id="element-1229" />
    </div>;
}